import React, { useState, useEffect } from 'react';
import propertyService from '../../services/propertyService';
import './AdminDashboard.css';

// Additional CSS styles for the ReviewListingsTab
const reviewListingStyles = `
  /* Modal styling */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1050;
    overflow-y: auto;
    padding: 20px;
  }
  
  .modal-dialog {
    background-color: white;
    border-radius: 8px;
    max-width: 900px;
    width: 100%;
    position: relative;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
    max-height: 90vh;
    display: flex;
    flex-direction: column;
  }
  
  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 20px;
    border-bottom: 1px solid #eee;
    position: relative;
  }
  
  .modal-body {
    padding: 20px;
    overflow-y: auto;
  }
  
  .modal-footer {
    padding: 15px 20px;
    border-top: 1px solid #eee;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }
  
  .modal-close {
    background: none;
    border: none;
    font-size: 28px;
    line-height: 1;
    cursor: pointer;
    color: #666;
    position: absolute;
    right: 15px;
    top: 10px;
    z-index: 10;
  }
  
  .modal-close:hover {
    color: #000;
  }
  .property-review-overview {
    border-bottom: 1px solid #eee;
    padding-bottom: 20px;
    margin-bottom: 20px;
  }
  
  .property-review-details {
    margin-top: 15px;
  }
  
  .detail-item {
    display: flex;
    margin-bottom: 8px;
  }
  
  .detail-item .label {
    font-weight: 500;
    min-width: 140px;
    color: #666;
  }
  
  .detail-item .value {
    flex: 1;
  }
  
  .approval-actions {
    margin-top: 20px;
    border-top: 1px solid #eee;
    padding-top: 20px;
  }
  
  .action-buttons {
    display: flex;
    gap: 10px;
  }
  
  .property-details-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 20px;
  }
  
  .detail-section {
    margin-bottom: 20px;
  }
  
  .detail-section h6 {
    font-weight: 600;
    margin-bottom: 10px;
    border-bottom: 1px solid #eee;
    padding-bottom: 5px;
  }
  
  .features-list {
    list-style-type: none;
    padding-left: 0;
  }
  
  .features-list li {
    position: relative;
    padding-left: 20px;
    margin-bottom: 5px;
  }
  
  .features-list li:before {
    content: "✓";
    color: #4CAF50;
    position: absolute;
    left: 0;
  }
  
  .image-gallery {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 10px;
    margin-top: 10px;
  }
  
  .gallery-image {
    width: 100%;
    height: 80px;
    position: relative;
  }
  
  .gallery-image img {
    width: 100%;
    height: 80px;
    object-fit: cover;
    border-radius: 4px;
  }
  
  .more-images-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);
    color: white;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 14px;
    font-weight: 600;
    border-radius: 4px;
  }
  
  .more-images-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);
    color: white;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 14px;
    font-weight: 600;
    border-radius: 4px;
  }
  
  .emoji-large {
    font-size: 3rem;
    display: inline-block;
    margin-bottom: 10px;
  }
`;

// Add styles to document
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = reviewListingStyles;
  document.head.appendChild(styleElement);
}

const ReviewListingsTab = ({ onPropertyChanged }) => {
  const [pendingProperties, setPendingProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [neighborhoods, setNeighborhoods] = useState([]);
  
  // Property being reviewed
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  
  // Review form data
  const [reviewData, setReviewData] = useState({
    neighborhood: '',
    latitude: '',
    longitude: '',
    adminFeedback: '',
    approvalAction: 'approve' // 'approve' or 'reject'
  });

  // Fetch pending properties and neighborhoods on component mount
  useEffect(() => {
    fetchPendingProperties();
    fetchNeighborhoods();
  }, []);

  // Replace the current fetchPendingProperties function with this one:

const fetchPendingProperties = async () => {
  setIsLoading(true);
  setErrorMessage('');
  
  try {
    // Use the new API method for pending properties
    const response = await propertyService.getPendingProperties();
    
    if (response && response.data) {
      setPendingProperties(response.data);
    } else if (Array.isArray(response)) {
      setPendingProperties(response);
    } else {
      setPendingProperties([]);
    }
  } catch (error) {
    console.error('Error fetching pending properties:', error);
    setErrorMessage('Could not fetch pending properties. Please try again.');
  } finally {
    setIsLoading(false);
  }
};
  
  const fetchNeighborhoods = async () => {
    try {
      const response = await propertyService.getNeighborhoods();
      if (response && response.data) {
        setNeighborhoods(response.data);
      }
    } catch (error) {
      console.error('Error fetching neighborhoods:', error);
      // Don't block the UI for neighborhood fetch failure
    }
  };

  const openReviewModal = (property) => {
    setSelectedProperty(property);
    
    // Pre-populate review data with existing values
    setReviewData({
      neighborhood: property.neighborhoodId || property.neighborhood || '',
      latitude: property.location?.coordinates?.[1] || '',
      longitude: property.location?.coordinates?.[0] || '',
      adminFeedback: '',
      approvalAction: 'approve'
    });
    
    setShowReviewModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setReviewData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleApprovalAction = (action) => {
    setReviewData(prev => ({
      ...prev,
      approvalAction: action
    }));
  };

  // Replace the current handleReviewSubmit function with this one:

const handleReviewSubmit = async () => {
  if (!selectedProperty) return;
  
  // Validation
  if (reviewData.approvalAction === 'approve') {
    if (!reviewData.neighborhood) {
      setErrorMessage('Please select a neighborhood before approving.');
      return;
    }
    if (!reviewData.latitude || !reviewData.longitude) {
      setErrorMessage('Please provide location coordinates before approving.');
      return;
    }
  } else if (reviewData.approvalAction === 'reject' && !reviewData.adminFeedback) {
    setErrorMessage('Please provide feedback when rejecting a property.');
    return;
  }
  
  setIsLoading(true);
  
  try {
    const propertyId = selectedProperty._id;
    
    if (reviewData.approvalAction === 'approve') {
      // Use the new API method to approve the property
      await propertyService.approvePendingProperty(propertyId, {
        neighborhood: reviewData.neighborhood,
        location: {
          type: 'Point',
          coordinates: [
            parseFloat(reviewData.longitude),
            parseFloat(reviewData.latitude)
          ]
        },
        adminFeedback: reviewData.adminFeedback
      });
      
      setSuccessMessage('Property approved and published successfully!');
    } else {
      // Use the new API method to reject the property
      await propertyService.rejectPendingProperty(propertyId, {
        adminFeedback: reviewData.adminFeedback
      });
      
      setSuccessMessage('Property rejected with feedback.');
    }
    
    // Refresh the list
    fetchPendingProperties();
    
    // Notify parent of the change
    if (onPropertyChanged) {
      onPropertyChanged();
    }
    
    // Close the modal
    setShowReviewModal(false);
    
    // Auto-hide success message after 3 seconds
    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  } catch (error) {
    console.error('Error updating property:', error);
    setErrorMessage(`Failed to process property: ${error.message || 'Unknown error'}`);
  } finally {
    setIsLoading(false);
  }
};

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <div>
      <h2 className="section-title">Review Agent Listings</h2>
      <p className="section-description">
        Review and approve property listings submitted by agents. 
        Assign neighborhoods and verify location coordinates before publishing.
      </p>
      
      {successMessage && (
        <div className="alert alert-success">
          {successMessage}
        </div>
      )}
      
      {errorMessage && (
        <div className="alert alert-danger">
          {errorMessage}
          <button 
            className="btn-link ms-2"
            onClick={() => setErrorMessage('')}
          >
            Dismiss
          </button>
        </div>
      )}
      
      {/* Pending Properties List */}
      {isLoading && !showReviewModal ? (
        <div className="text-center p-6">
          <div className="loading-spinner"></div>
          <p className="mt-3">Loading pending properties...</p>
        </div>
      ) : pendingProperties.length === 0 ? (
        <div className="text-center p-6 bg-gray-50 rounded">
          <div className="mb-3">
            <span className="emoji-large">🎉</span>
          </div>
          <h4>All caught up!</h4>
          <p className="text-muted">There are no properties waiting for review.</p>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th>Property</th>
                <th>Agent</th>
                <th>Details</th>
                <th>Submitted</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pendingProperties.map((property) => (
                <tr key={property._id}>
                  <td className="property-cell">
                    <div className="property-info">
                      <div className="property-image">
                        <img 
                          src={property.mainImage || 'https://via.placeholder.com/40?text=Property'} 
                          alt={property.address}
                        />
                      </div>
                      <div className="property-details">
                        <div className="property-address">{property.address}</div>
                        <div className="property-type">
                          {property.propertyType} · {property.listingType === 'purchase' ? 'For Sale' : 'For Rent'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    {property.listedBy?.agentName || 'Unknown agent'}
                    <div className="text-muted small">
                      {property.listedBy?.agency || ''}
                    </div>
                  </td>
                  <td>
                    {property.bedrooms} bed / {property.bathrooms} bath
                    <br />
                    <span className="property-sqft">{property.sqft?.toLocaleString() || 0} sq ft · {property.price}</span>
                  </td>
                  <td>{formatDate(property.submittedAt || property.createdAt)}</td>
                  <td className="text-right">
                    <button 
                      className="btn btn-primary btn-sm"
                      onClick={() => openReviewModal(property)}
                    >
                      Review
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Review Modal */}
      {showReviewModal && selectedProperty && (
        <div className="modal-overlay">
          <div className="modal-dialog modal-lg">
            <div className="modal-header">
              <h3>Review Property Listing</h3>
              <button 
                className="modal-close"
                onClick={() => setShowReviewModal(false)}
                style={{ 
                  position: 'absolute', 
                  right: '15px', 
                  top: '15px',
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer'
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {/* Property Overview */}
              <div className="property-review-overview mb-4">
                <div className="row">
                  <div className="col-md-5">
                    {selectedProperty.mainImage ? (
                      <img 
                        src={selectedProperty.mainImage} 
                        alt={selectedProperty.address}
                        className="img-fluid rounded"
                        style={{ 
                          width: '100%', 
                          height: '200px', 
                          objectFit: 'cover',
                          objectPosition: 'center'
                        }}
                      />
                    ) : (
                      <div className="placeholder-image">No image available</div>
                    )}
                  </div>
                  <div className="col-md-7">
                    <h4>{selectedProperty.address}</h4>
                    <div className="property-review-details">
                      <div className="detail-item">
                        <span className="label">Price:</span>
                        <span className="value">{selectedProperty.price}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Type:</span>
                        <span className="value">{selectedProperty.propertyType}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Listing:</span>
                        <span className="value">{selectedProperty.listingType === 'purchase' ? 'For Sale' : 'For Rent'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Bedrooms:</span>
                        <span className="value">{selectedProperty.bedrooms}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Bathrooms:</span>
                        <span className="value">{selectedProperty.bathrooms}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Square Footage:</span>
                        <span className="value">{selectedProperty.sqft?.toLocaleString() || 0} sq ft</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Submitted By:</span>
                        <span className="value">{selectedProperty.listedBy?.agentName || 'Unknown agent'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Review Form */}
              <div className="review-form mb-4">
                <h5>Admin Review</h5>
                <hr />
                
                <div className="form-group mb-3">
                  <label className="form-label">Assign Neighborhood</label>
                  <select
                    name="neighborhood"
                    value={reviewData.neighborhood}
                    onChange={handleInputChange}
                    className="form-control"
                    required
                  >
                    <option value="">Select a neighborhood</option>
                    {neighborhoods.map((neighborhood) => (
                      <option 
                        key={neighborhood._id} 
                        value={neighborhood._id}
                      >
                        {neighborhood.name} ({neighborhood.city}, {neighborhood.state})
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="form-label">Latitude</label>
                      <input
                        type="text"
                        name="latitude"
                        value={reviewData.latitude}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="e.g. 37.7749"
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="form-label">Longitude</label>
                      <input
                        type="text"
                        name="longitude"
                        value={reviewData.longitude}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="e.g. -122.4194"
                        required
                      />
                    </div>
                  </div>
                </div>
                
                <div className="form-group mb-3">
                  <label className="form-label">Admin Feedback (required for rejections)</label>
                  <textarea
                    name="adminFeedback"
                    value={reviewData.adminFeedback}
                    onChange={handleInputChange}
                    className="form-control"
                    rows="3"
                    placeholder="Provide feedback to the agent (especially if rejecting the listing)"
                  ></textarea>
                </div>
                
                <div className="approval-actions">
                  <div className="action-buttons">
                    <button
                      type="button"
                      className={`btn ${reviewData.approvalAction === 'approve' ? 'btn-success active' : 'btn-outline-success'}`}
                      onClick={() => handleApprovalAction('approve')}
                    >
                      Approve Listing
                    </button>
                    <button
                      type="button"
                      className={`btn ${reviewData.approvalAction === 'reject' ? 'btn-danger active' : 'btn-outline-danger'}`}
                      onClick={() => handleApprovalAction('reject')}
                    >
                      Reject Listing
                    </button>
                  </div>
                  <div className="action-description mt-2">
                    {reviewData.approvalAction === 'approve' ? (
                      <p className="text-success small">
                        <i className="bi bi-info-circle me-1"></i>
                        Approving will publish this listing with the assigned neighborhood and make it visible to customers.
                      </p>
                    ) : (
                      <p className="text-danger small">
                        <i className="bi bi-exclamation-triangle me-1"></i>
                        Rejecting will return this listing to the agent with your feedback. They can make changes and resubmit.
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Property Details Section */}
              <div className="property-additional-details">
                <div className="accordion" id="propertyDetailsAccordion">
                  <div className="accordion-item">
                    <h2 className="accordion-header">
                      <button 
                        className="accordion-button collapsed" 
                        type="button" 
                        data-bs-toggle="collapse" 
                        data-bs-target="#collapseDetails"
                      >
                        View Full Property Details
                      </button>
                    </h2>
                    <div 
                      id="collapseDetails" 
                      className="accordion-collapse collapse"
                      data-bs-parent="#propertyDetailsAccordion"
                    >
                      <div className="accordion-body">
                        <div className="property-details-grid">
                          <div className="detail-section">
                            <h6>Basic Information</h6>
                            <p>{selectedProperty.description || 'No description provided.'}</p>
                            
                            <div className="detail-item">
                              <span className="label">Year Built:</span>
                              <span className="value">{selectedProperty.yearBuilt || 'Not specified'}</span>
                            </div>
                            <div className="detail-item">
                              <span className="label">Lot Size:</span>
                              <span className="value">{selectedProperty.lotSize ? `${selectedProperty.lotSize.toLocaleString()} sq ft` : 'Not specified'}</span>
                            </div>
                          </div>
                          
                          <div className="detail-section">
                            <h6>Features</h6>
                            {selectedProperty.features && selectedProperty.features.length > 0 ? (
                              <ul className="features-list">
                                {selectedProperty.features.map((feature, index) => (
                                  <li key={index}>{feature}</li>
                                ))}
                              </ul>
                            ) : (
                              <p>No features specified</p>
                            )}
                          </div>
                          
                          {selectedProperty.listingType === 'rent' && (
                            <div className="detail-section">
                              <h6>Pet Policy</h6>
                              <div className="detail-item">
                                <span className="label">Dogs:</span>
                                <span className="value">{selectedProperty.petPolicy?.allowsDogs ? 'Allowed' : 'Not allowed'}</span>
                              </div>
                              <div className="detail-item">
                                <span className="label">Cats:</span>
                                <span className="value">{selectedProperty.petPolicy?.allowsCats ? 'Allowed' : 'Not allowed'}</span>
                              </div>
                              {(selectedProperty.petPolicy?.allowsDogs || selectedProperty.petPolicy?.allowsCats) && (
                                <>
                                  <div className="detail-item">
                                    <span className="label">Pet Deposit:</span>
                                    <span className="value">${selectedProperty.petPolicy?.petDeposit || 0}</span>
                                  </div>
                                  <div className="detail-item">
                                    <span className="label">Pet Rent:</span>
                                    <span className="value">${selectedProperty.petPolicy?.petRent || 0}/month</span>
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                          
                          <div className="detail-section">
                            <h6>Additional Details</h6>
                            <div className="detail-item">
                              <span className="label">Parking:</span>
                              <span className="value">
                                {selectedProperty.parking?.type || 'Not specified'}
                                {selectedProperty.parking?.spaces > 0 && ` (${selectedProperty.parking.spaces} spaces)`}
                              </span>
                            </div>
                            <div className="detail-item">
                              <span className="label">Laundry:</span>
                              <span className="value">{selectedProperty.laundry?.type || 'Not specified'}</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Image Gallery */}
                        {selectedProperty.images && selectedProperty.images.length > 0 && (
                          <div className="property-images mt-4">
                            <h6>Property Images ({selectedProperty.images.length})</h6>
                            <div className="image-gallery">
                              {selectedProperty.images.slice(0, 8).map((imageUrl, index) => (
                                <div key={index} className="gallery-image">
                                  <img 
                                    src={imageUrl} 
                                    alt={`${selectedProperty.address} - Image ${index + 1}`}
                                    className="img-thumbnail"
                                    style={{ 
                                      width: '100%', 
                                      height: '80px', 
                                      objectFit: 'cover' 
                                    }}
                                  />
                                </div>
                              ))}
                              {selectedProperty.images.length > 8 && (
                                <div className="gallery-image more-images">
                                  <div className="more-images-overlay">
                                    +{selectedProperty.images.length - 8} more
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowReviewModal(false)}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleReviewSubmit}
                disabled={isLoading}
              >
                {isLoading ? 'Processing...' : (
                  reviewData.approvalAction === 'approve' ? 'Approve & Publish' : 'Send Rejection'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewListingsTab;